import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import {
    PageLocale,
    TeachingMaterial
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { IconDoc48 } from '@cineamo/ui/src/icons/ic-doc';
import { IconDownload24 } from '@cineamo/ui/src/icons/ic-download';
import { IconGif48 } from '@cineamo/ui/src/icons/ic-gif';
import { IconJpg48 } from '@cineamo/ui/src/icons/ic-jpg';
import { IconMov48 } from '@cineamo/ui/src/icons/ic-mov';
import { IconMp348 } from '@cineamo/ui/src/icons/ic-mp3';
import { IconPdf48 } from '@cineamo/ui/src/icons/ic-pdf';
import { IconPng48 } from '@cineamo/ui/src/icons/ic-png';
import { IconPpt48 } from '@cineamo/ui/src/icons/ic-ppt';

const TeachingMaterialItem: React.FC<{ material: TeachingMaterial }> = ({ material }) => {
    const { domainConfiguration } = useDomainConfiguration();
    const t = useTranslations();
    const Icon = useMemo(() => {
        switch (material.format?.toLowerCase()) {
            case 'pdf':
                return IconPdf48;
            case 'ppt':
            case 'pptx':
                return IconPpt48;
            case 'doc':
            case 'docx':
                return IconDoc48;
            case 'jpg':
                return IconJpg48;
            case 'png':
                return IconPng48;
            case 'gif':
                return IconGif48;
            case 'mp3':
                return IconMp348;
            case 'mov':
                return IconMov48;
            default:
                return null;
        }
    }, [material.format]);

    return (
        <div
            key={material.title}
            className="flex flex-row space-x-24 items-center gap-2">
            <Icon
                className="w-36 h-36 shrink-0"
                aria-hidden="true"
            />
            <div className="grow py-12">
                <div className="text-body-bold-14">{material.title}</div>
                <div className="text-body-14 opacity-70">{material.description}</div>
            </div>
            <Link
                href={material.downloadLink}
                download={material.downloadLink}
                target="_blank"
                className="bg-[var(--primary-color)] p-[9px] rounded-[3px]"
                rel="noopener noreferrer"
                aria-label={t('common.download-file', { title: material.title })}>
                <IconDownload24
                    fill={domainConfiguration.style.onPrimaryColor}
                    className="w-[18px] h-[18px]"
                    aria-hidden="true"
                />
            </Link>
        </div>
    );
};

const MovieTeachingMaterials: React.FC = () => {
    const { domainConfiguration } = useDomainConfiguration();
    const locale = useLocale();
    const teachingMaterials = domainConfiguration.teachingMaterials?.[locale as PageLocale];
    if (!teachingMaterials || !teachingMaterials.materials?.length) {
        return null;
    }
    return (
        <div>
            <div>
                <style>
                    {`.rich a {
                        color: var(--primary-color);
                        text-decoration: underline;
                        font-weight: bold;
                    }`}
                </style>
                <div
                    className="text-body-14 mb-24 rich"
                    dangerouslySetInnerHTML={{ __html: teachingMaterials.description }}
                />
            </div>
            <div className="flex flex-col space-y-8">
                {teachingMaterials.materials.map((material, index) => (
                    <TeachingMaterialItem
                        key={`${material.title}-${index}`}
                        material={material}
                    />
                ))}
            </div>
        </div>
    );
};

export default MovieTeachingMaterials;
